import { apiClient } from './client';
import type { 
  CashPaymentConfigRequest, 
  CashPaymentConfigResponse,
  CashPaymentApiResponse 
} from './types/cashPayment';

/**
 * Cash Payment Configuration API Service
 * Handles all cash payment configuration operations for companies
 */

/**
 * Get current cash payment configuration for the authenticated company
 */
export const getCashPaymentConfig = async (): Promise<CashPaymentApiResponse<CashPaymentConfigResponse | null>> => {
  try {
    const response = await apiClient.get<CashPaymentConfigResponse>('/payment/cash-payment-config/');
    
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error('Error fetching cash payment config:', error);
    return {
      success: false,
      error: 'Error al obtener la configuración de pagos presenciales',
    };
  }
};

/**
 * Create new cash payment configuration
 */
export const createCashPaymentConfig = async (
  config: CashPaymentConfigRequest
): Promise<CashPaymentApiResponse<CashPaymentConfigResponse>> => {
  try {
    const response = await apiClient.post<CashPaymentConfigResponse>('/payment/cash-payment-config/', config);
    
    return {
      success: true,
      data: response,
      message: 'Configuración de pagos presenciales creada exitosamente',
    };
  } catch (error) {
    console.error('Error creating cash payment config:', error);
    return {
      success: false,
      error: 'Error al crear la configuración de pagos presenciales',
    };
  }
};

/**
 * Update existing cash payment configuration
 */
export const updateCashPaymentConfig = async (
  id: number,
  config: CashPaymentConfigRequest
): Promise<CashPaymentApiResponse<CashPaymentConfigResponse>> => {
  try {
    const response = await apiClient.put<CashPaymentConfigResponse>(`/payment/cash-payment-config/${id}/`, config);
    
    return {
      success: true,
      data: response,
      message: 'Configuración de pagos presenciales actualizada exitosamente',
    };
  } catch (error) {
    console.error('Error updating cash payment config:', error);
    return {
      success: false,
      error: 'Error al actualizar la configuración de pagos presenciales',
    };
  }
};

/**
 * Deactivate cash payment configuration (soft delete)
 */
export const deactivateCashPaymentConfig = async (
  id: number
): Promise<CashPaymentApiResponse<void>> => {
  try {
    await apiClient.delete<void>(`/payment/cash-payment-config/${id}/`);
    
    return {
      success: true,
      message: 'Configuración de pagos presenciales desactivada exitosamente',
    };
  } catch (error) {
    console.error('Error deactivating cash payment config:', error);
    return {
      success: false,
      error: 'Error al desactivar la configuración de pagos presenciales',
    };
  }
};

/**
 * Reactivate cash payment configuration
 */
export const reactivateCashPaymentConfig = async (
  id: number
): Promise<CashPaymentApiResponse<CashPaymentConfigResponse>> => {
  try {
    const response = await apiClient.post<CashPaymentConfigResponse>(`/payment/cash-payment-config/${id}/reactivate/`, {});
    
    return {
      success: true,
      data: response,
      message: 'Configuración de pagos presenciales reactivada exitosamente',
    };
  } catch (error) {
    console.error('Error reactivating cash payment config:', error);
    return {
      success: false,
      error: 'Error al reactivar la configuración de pagos presenciales',
    };
  }
};
